import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import iso from "i18n-iso-countries";

import { queryKeys } from "~/v2/api/queryKeys";
import { getCountries, getCountryInfos } from "~/v2/api/rest/onboarding";
import type { Country } from "~/v2/types";

const STALE_TIME =
  1000 * // 1 second
  60 * // 1 minute
  60 * // 1 hour
  24 * // 1 day
  30; // 30 days

export const useCountry = () => {
  const { data: allowedCountries = [], isLoading: isAllowedCountriesLoading } =
    useQuery({
      queryKey: queryKeys.countries(),
      queryFn: getCountries,
      staleTime: STALE_TIME,
    });

  const { data: countryInfos = {}, isLoading: isCountryInfosLoading } =
    useQuery({
      queryKey: queryKeys.countryInfos(),
      queryFn: getCountryInfos,
      staleTime: STALE_TIME,
    });

  const allCountries: Country[] = useMemo(
    () =>
      Object.entries(countryInfos).map(
        ([alpha3, countryInfo]): Country => ({
          "alpha-3": alpha3,
          "country-code": iso.alpha3ToNumeric(alpha3),
          name: countryInfo.full_name,
        }),
      ),
    [countryInfos],
  );

  return {
    /** Countries that Alpaca supports - ie allowed as tax residence  */
    allowedCountries,
    /** All countries on Earth */
    allCountries,
    alpha3ToName: (alpha3: string) =>
      allCountries.find(({ "alpha-3": short }) => short === alpha3)?.name,
    nameToAlpha3: (name: string) =>
      allCountries.find(({ name: n }) => n === name)?.["alpha-3"],
    infos: countryInfos,
    canLimitedFund: (alpha3: string) => alpha3 === "USA",
    isLoading: isAllowedCountriesLoading || isCountryInfosLoading,
  };
};

export type UseCountryReturn = ReturnType<typeof useCountry>;
